import { GUID, PersonOrg, TodayPriorityCompletionView, TodayPriorityType } from '@ndvr/customer-api';

import { convertBackendTimeToBrowser, parseLocalDate } from '@/utils';

import { Priority, PriorityType } from './priority-types';

export const getPriorityDate = (priority: Priority): Date => {
    switch (priority.type) {
        case PriorityType.TASK:
            return parseLocalDate(priority.data.dueDate);
        case PriorityType.EVENT:
        case PriorityType.FOLLOWUP:
            return convertBackendTimeToBrowser(priority.data.startTime, priority.data.startTimeZone);
    }
};

export const sortPrioritiesChronologically = (priorities: Priority[]) =>
    [...priorities].sort((a, b) => getPriorityDate(a).getTime() - getPriorityDate(b).getTime());

export const sortPrioritiesByUrgency = (priorities: Priority[], now = new Date()) =>
    [...priorities].sort((a, b) => {
        const aDate = getPriorityDate(a);
        const bDate = getPriorityDate(b);
        const aDistance = Math.abs(aDate.getTime() - now.getTime());
        const bDistance = Math.abs(bDate.getTime() - now.getTime());

        if (aDistance !== bDistance) {
            return aDistance - bDistance;
        }

        return aDate.getTime() - bDate.getTime();
    });

export const getPriorityKey = (priority: Priority) => {
    switch (priority.type) {
        case PriorityType.TASK:
            return `${priority.type}-${priority.data.taskGUID}`;
        case PriorityType.EVENT:
        case PriorityType.FOLLOWUP:
            return `${priority.type}-${priority.data.eventGUID}`;
    }
};

export const getPriorityCompletionKey = (targetGUID: string, type: TodayPriorityType) => `${targetGUID}-${type}`;

export const getPriorityCompletionKeyForPriority = (priority: Priority) => {
    switch (priority.type) {
        case PriorityType.EVENT:
            return getPriorityCompletionKey(priority.data.eventGUID, TodayPriorityType.EVENT_PREP);
        case PriorityType.FOLLOWUP:
            return getPriorityCompletionKey(priority.data.eventGUID, TodayPriorityType.EVENT_FOLLOWUP);
        case PriorityType.TASK:
            return getPriorityCompletionKey(priority.data.taskGUID, TodayPriorityType.TASK);
    }
};

export const getCompletionTypeForPriority = (priority: Priority): TodayPriorityType => {
    switch (priority.type) {
        case PriorityType.EVENT:
            return TodayPriorityType.EVENT_PREP;
        case PriorityType.FOLLOWUP:
            return TodayPriorityType.EVENT_FOLLOWUP;
        case PriorityType.TASK:
            return TodayPriorityType.TASK;
    }
};

export const getPriorityTargetGUID = (priority: Priority): string => {
    switch (priority.type) {
        case PriorityType.TASK:
            return priority.data.taskGUID;
        case PriorityType.EVENT:
        case PriorityType.FOLLOWUP:
            return priority.data.eventGUID;
    }
};

export const getCompletionKeys = (completions: TodayPriorityCompletionView[]) =>
    new Set(completions.map((completion) => getPriorityCompletionKey(completion.targetGUID, completion.type)));

export const isAssignedToMe = (priority: Priority, userId: GUID<PersonOrg> | undefined): boolean => {
    if (!userId) {
        return false;
    }

    switch (priority.type) {
        case PriorityType.TASK: {
            return priority.data.assignee === userId;
        }

        case PriorityType.EVENT:
        case PriorityType.FOLLOWUP: {
            return priority.data.organizerPersonOrgGUID === userId;
        }
    }
};

export const shouldShowCreateTaskAction = (priority: Priority) => priority.type !== PriorityType.TASK;
